import React, { useMemo } from 'react';
import { Card, CardBody, CardHeader, Row, Col } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faThumbsUp, faThumbsDown, faNewspaper } from '@fortawesome/free-solid-svg-icons';
import { FacebookMetricsChart } from './FacebookMetricsChart';
import moment from 'moment';

interface LikesBalanceProps {
  facebookInsights: any;
  selectedPeriod: string;
}

export const FacebookLikesBalance: React.FC<LikesBalanceProps> = ({
  facebookInsights,
  selectedPeriod
}) => {
  // Calcular métricas de balance de likes
  const likesMetrics = useMemo(() => {
    if (!facebookInsights) return null;

    const totalLikes = facebookInsights.page_fans?.values?.[0] || 0;
    const previousLikes = facebookInsights.page_fans?.values?.[1] || 0;
    const acquiredLikes = Math.max(0, totalLikes - previousLikes);
    const lostLikes = Math.max(0, previousLikes - totalLikes);
    const totalContent = facebookInsights.page_posts?.values?.length || 0;

    return {
      acquired: acquiredLikes,
      lost: lostLikes,
      totalContent,
      change: totalLikes - previousLikes
    };
  }, [facebookInsights]);

  // Datos para el gráfico
  const chartData = useMemo(() => {
    if (!facebookInsights?.page_fans?.values) return { labels: [], datasets: [] };

    const labels = facebookInsights.page_fans.values.map((_: any, index: number) => {
      const date = moment().subtract(facebookInsights.page_fans.values.length - index - 1, 'days');
      return date.format('DD MMM');
    });

    return {
      labels,
      datasets: [
        {
          label: 'Adquiridos',
          data: facebookInsights.page_fans.values.map((_: any, index: number) => 
            index > 0 ? Math.max(0, Number(facebookInsights.page_fans.values[index]) - Number(facebookInsights.page_fans.values[index - 1])) : 0
          ),
          borderColor: '#1877F2',
          backgroundColor: 'rgba(24, 119, 242, 0.1)',
          fill: true
        },
        {
          label: 'Perdidos',
          data: facebookInsights.page_fans.values.map((_: any, index: number) => 
            index > 0 ? Math.max(0, Number(facebookInsights.page_fans.values[index - 1]) - Number(facebookInsights.page_fans.values[index])) : 0
          ),
          borderColor: '#E4405F',
          backgroundColor: 'rgba(228, 64, 95, 0.1)',
          fill: true
        },
        {
          label: 'Contenido total',
          data: facebookInsights.page_posts?.values?.map(() => 1) || [],
          borderColor: '#F59E0B',
          backgroundColor: 'rgba(245, 158, 11, 0.1)',
          fill: true
        }
      ]
    };
  }, [facebookInsights]);

  if (!likesMetrics) {
    return (
      <Card>
        <CardHeader>
          <h4>Balance de Me gusta</h4>
        </CardHeader>
        <CardBody>
          <div className="no-data">
            <FontAwesomeIcon icon={faThumbsUp} size="2x" />
            <p>No hay datos disponibles</p>
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <div className="likes-balance">
      <Card>
        <CardHeader>
          <h4>Balance de Me gusta</h4>
        </CardHeader>
        <CardBody>
          {/* Métricas de resumen */}
          <Row className="mb-4">
            <Col md={4}>
              <div className="metric-summary acquired">
                <div className="metric-icon">
                  <FontAwesomeIcon icon={faThumbsUp} />
                </div>
                <div className="metric-info">
                  <div className="metric-value">{likesMetrics.acquired}</div>
                  <div className="metric-label">Adquiridos</div>
                  {likesMetrics.acquired > 0 && (
                    <div className="metric-change increase">
                      <FontAwesomeIcon icon="arrow-up" />
                    </div>
                  )}
                </div>
              </div>
            </Col>
            <Col md={4}>
              <div className="metric-summary lost">
                <div className="metric-icon">
                  <FontAwesomeIcon icon={faThumbsDown} />
                </div>
                <div className="metric-info">
                  <div className="metric-value">{likesMetrics.lost}</div>
                  <div className="metric-label">Perdidos</div>
                  {likesMetrics.lost > 0 && (
                    <div className="metric-change decrease">
                      <FontAwesomeIcon icon="arrow-down" />
                    </div>
                  )}
                </div>
              </div>
            </Col>
            <Col md={4}>
              <div className="metric-summary content">
                <div className="metric-icon">
                  <FontAwesomeIcon icon={faNewspaper} />
                </div>
                <div className="metric-info">
                  <div className="metric-value">{likesMetrics.totalContent}</div>
                  <div className="metric-label">Contenido total</div>
                  {likesMetrics.totalContent > 0 && (
                    <div className="metric-change decrease">
                      <FontAwesomeIcon icon="arrow-down" />
                    </div>
                  )}
                </div>
              </div>
            </Col>
          </Row>

          {/* Gráfico */}
          <FacebookMetricsChart
            title=""
            data={chartData}
            type="line"
            height={250}
          />
        </CardBody>
      </Card>
    </div>
  );
};
